// src/components/NotificationPopup.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { X, Bell, ArrowRight, Megaphone } from 'lucide-react';

const NotificationPopup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notification, setNotification] = useState(null);
  const [show, setShow] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5);

  useEffect(() => {
    const fetchLatest = async () => {
      if (!user) {
        console.log('📢 [Popup] User not logged in');
        return;
      }

      console.log('📢 [Popup] User UID:', user.uid);

      try {
        // Fetch all notifications (simple query, no compound index needed)
        const snap = await getDocs(collection(db, 'notifications'));
        const allNotifications = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        console.log('📢 [Popup] Total notifications fetched:', allNotifications.length);

        // Filter for this user
        const myNotifs = allNotifications.filter(n =>
          Array.isArray(n.recipients) && n.recipients.includes(user.uid)
        );

        console.log('📢 [Popup] My notifications:', myNotifs.length);

        if (myNotifs.length === 0) return;

        // Sort by createdAt (newest first) manually
        myNotifs.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        const latest = myNotifs[0];
        console.log('📢 [Popup] Latest notification:', latest);

        // Check if already seen (localStorage)
        const seenKey = `notif_seen_${latest.id}_${user.uid}`;
        const alreadyShown = localStorage.getItem(seenKey);

        if (!alreadyShown) {
          setTimeout(() => {
            setNotification(latest);
            setShow(true);
            setTimeLeft(5);
          }, 2000);
        } else {
          console.log('📢 [Popup] Already seen this notification');
        }
      } catch (error) {
        console.error('📢 [Popup] Error:', error);
      }
    };

    fetchLatest();
  }, [user]);

  useEffect(() => {
    if (!show) return;
    if (timeLeft <= 0) {
      handleClose();
      return;
    }
    const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [show, timeLeft]);

  const handleClose = () => {
    if (notification && user) {
      const seenKey = `notif_seen_${notification.id}_${user.uid}`;
      localStorage.setItem(seenKey, 'true');
    }
    setShow(false);
    setNotification(null);
  };

  const handleClick = () => {
    handleClose();
    navigate('/notifications');
  };

  if (!show || !notification) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 anim-fadeIn"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={handleClose}
    >
      <div
        className="rounded-3xl shadow-2xl max-w-md w-full overflow-hidden anim-popIn relative"
        style={{ backgroundColor: 'var(--color-card-bg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: 'var(--color-border)' }}>
          <div
            className="h-full transition-all duration-1000"
            style={{
              backgroundColor: 'var(--color-accent)',
              width: `${(timeLeft / 5) * 100}%`,
            }}
          ></div>
        </div>

        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-black/10"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)', color: 'var(--color-primary-text)' }}
        >
          <X size={18} />
        </button>

        {notification.image && (
          <div className="relative h-48 md:h-56 overflow-hidden">
            <img
              src={notification.image}
              alt={notification.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>
        )}

        <div className="p-6 text-center">
          {!notification.image && (
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center anim-pulseGlow"
                style={{ backgroundColor: 'rgba(22, 163, 74, 0.12)' }}>
                <Megaphone size={32} style={{ color: '#16a34a' }} />
              </div>
            </div>
          )}

          {notification.image && (
            <div className="flex justify-center -mt-12 mb-4 relative z-10">
              <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-xl"
                style={{ backgroundColor: '#16a34a' }}>
                <Bell size={24} style={{ color: '#fff' }} />
              </div>
            </div>
          )}

          <h2 className="text-xl md:text-2xl font-bold mb-2"
            style={{ color: 'var(--color-primary-text)' }}>
            {notification.title}
          </h2>

          <p className="text-sm leading-relaxed mb-5"
            style={{ color: 'var(--color-secondary-text)' }}>
            {notification.description}
          </p>

          <div className="flex flex-col gap-2">
            {notification.buttonText && notification.buttonLink && (
              <button
                onClick={() => {
                  handleClose();
                  navigate(notification.buttonLink);
                }}
                className="w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg ripple-btn"
                style={{
                  background: 'linear-gradient(135deg, #16a34a 0%, #14532d 100%)',
                  color: '#ffffff',
                }}
              >
                {notification.buttonText} <ArrowRight size={16} />
              </button>
            )}

            <button
              onClick={handleClick}
              className="w-full rounded-full py-3 font-semibold text-sm btn-2d transition"
              style={{
                border: `1px solid var(--color-border)`,
                color: 'var(--color-primary-text)',
                backgroundColor: 'transparent',
              }}
            >
              View in Inbox
            </button>
          </div>

          <p className="text-[10px] mt-3" style={{ color: 'var(--color-secondary-text)' }}>
            Auto-close in {timeLeft}s
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationPopup;